import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Exemplar } from '../types';
import { exemplarsApi, authApi, getThumbnailUrl, AuthUser } from '../services/api';
import './ExemplarStockView.css';

/**
 * Componente ExemplarStockView
 *
 * Consulta rápida e atualizada dos exemplares de caixas separados e
 * armazenados em Mauá (feiras e premiações):
 * - Login obrigatório para visualização (mesmo padrão do catálogo)
 * - Busca por OP, título ou observações
 * - Grade com foto, OP, título, quantidade e localização
 * - Vínculo opcional com o projeto do catálogo
 */
export function ExemplarStockView(): React.ReactElement {
  // Estado de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);

  // Verificar sessão ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const status = await authApi.getStatus();
        if (status.isAuthenticated && status.user) {
          setIsAuthenticated(true);
          setCurrentUser(status.user);
        }
      } catch {
        // Não autenticado
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const result = await authApi.login(loginUsername, loginPassword);
      setIsAuthenticated(true);
      setCurrentUser(result.user);
    } catch (err: any) {
      setLoginError(err?.message || 'Usuário ou senha inválidos');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignora erro
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setLoginUsername('');
    setLoginPassword('');
  };

  // Tela de loading inicial
  if (isCheckingAuth) {
    return (
      <div className="exemplar-stock-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="exemplar-stock-view__loading">
          <div className="exemplar-stock-view__spinner" />
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Estoque Mauá é dado interno — apenas administradores. A API bloqueia
  // viewers com 403 de qualquer forma; isto evita a tela quebrada.
  if (currentUser?.role !== 'admin') {
    return (
      <div className="exemplar-stock-view">
        <header className="exemplar-stock-view__header">
          <div className="exemplar-stock-view__header-left">
            <img src="/logo-ipsis-pack.png" alt="Ipsis Pack" className="exemplar-stock-view__logo" />
          </div>
        </header>
        <div className="exemplar-stock-view__restricted">
          <span className="exemplar-stock-view__restricted-icon">🔒</span>
          <h2 className="exemplar-stock-view__restricted-title">Acesso restrito</h2>
          <p className="exemplar-stock-view__restricted-text">
            O estoque Mauá é visível apenas para administradores.
          </p>
          <Link to="/" className="exemplar-stock-view__restricted-link">← Voltar ao catálogo</Link>
        </div>
      </div>
    );
  }

  // Tela de login
  if (!isAuthenticated) {
    return (
      <div className="exemplar-stock-view">
        <header className="exemplar-stock-view__header">
          <div className="exemplar-stock-view__header-left">
            <img src="/logo-ipsis-pack.png" alt="Ipsis Pack" className="exemplar-stock-view__logo" />
          </div>
        </header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '2rem' }}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '2.5rem', maxWidth: '400px', width: '100%', boxShadow: '0 10px 40px rgba(25,19,100,0.16)', border: '1px solid #e5e8f1' }}>
            <h2 style={{ textAlign: 'center', color: '#191364', fontFamily: 'var(--font-family-display)', fontSize: '1.875rem', fontWeight: 400, marginBottom: '0.25rem' }}>📦 Estoque Mauá</h2>
            <p style={{ textAlign: 'center', color: '#5a5d7a', fontSize: '0.9375rem', marginBottom: '1.5rem' }}>Faça login para consultar o estoque</p>
            <form onSubmit={handleLogin}>
              {loginError && (
                <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem', textAlign: 'center' }}>
                  {loginError}
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Usuário</label>
                <input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                  disabled={isLoggingIn}
                  required
                  style={{ width: '100%', padding: '0.875rem', border: '1px solid #d6d9e6', borderRadius: '8px', fontSize: '1rem', color: '#373a50' }}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Senha</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  disabled={isLoggingIn}
                  required
                  style={{ width: '100%', padding: '0.875rem', border: '1px solid #d6d9e6', borderRadius: '8px', fontSize: '1rem', color: '#373a50' }}
                />
              </div>
              <button
                type="submit"
                disabled={isLoggingIn || !loginUsername.trim() || !loginPassword.trim()}
                style={{ width: '100%', padding: '0.875rem', background: '#2f8a55', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {isLoggingIn ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Estoque (autenticado)
  return <ExemplarStockContent currentUser={currentUser} onLogout={handleLogout} />;
}

// Componente interno com o conteúdo do estoque (a view acima já restringe a admin)
function ExemplarStockContent({ currentUser, onLogout }: { currentUser: AuthUser | null; onLogout: () => void }): React.ReactElement {
  const [exemplars, setExemplars] = useState<Exemplar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const loadExemplars = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await exemplarsApi.getAll();
      setExemplars(data);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar o estoque. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExemplars();
  }, [loadExemplars]);

  // Filtro local por OP, título, observações ou projeto vinculado
  const filteredExemplars = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return exemplars;

    return exemplars.filter(ex =>
      ex.opNumber.toLowerCase().includes(term) ||
      ex.title.toLowerCase().includes(term) ||
      (ex.notes || '').toLowerCase().includes(term) ||
      (ex.locationDetail || '').toLowerCase().includes(term) ||
      (ex.book?.title || '').toLowerCase().includes(term)
    );
  }, [exemplars, searchText]);

  // Total de peças (soma das quantidades)
  const totalPieces = useMemo(
    () => filteredExemplars.reduce((sum, ex) => sum + ex.quantity, 0),
    [filteredExemplars]
  );

  return (
    <div className="exemplar-stock-view">
      {/* Header */}
      <header className="exemplar-stock-view__header">
        <div className="exemplar-stock-view__header-left">
          <img
            src="/logo-ipsis-pack.png"
            alt="Ipsis Pack"
            className="exemplar-stock-view__logo"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: '#fff', fontSize: '0.85rem' }}>
            Olá, {currentUser?.displayName || currentUser?.username}
          </span>
          <Link to="/admin" title="Área de Admin" className="exemplar-stock-view__admin-link">
            <span className="exemplar-stock-view__admin-link-icon">⚙️</span>
            <span className="exemplar-stock-view__admin-link-text">Área de Admin</span>
          </Link>
          <button
            type="button"
            onClick={onLogout}
            style={{ padding: '0.4rem 0.8rem', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' }}
          >
            Sair
          </button>
        </div>
      </header>

      <div className="exemplar-stock-view__content">
        {/* Barra de título + busca */}
        <div className="exemplar-stock-view__toolbar">
          <div>
            <h1 className="exemplar-stock-view__title">📦 Estoque Mauá</h1>
            <p className="exemplar-stock-view__subtitle">
              Exemplares separados para feiras e premiações
            </p>
          </div>
          <div className="exemplar-stock-view__search">
            <span className="exemplar-stock-view__search-icon">🔍</span>
            <input
              type="text"
              className="exemplar-stock-view__search-input"
              placeholder="Buscar por OP, título ou projeto..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                type="button"
                className="exemplar-stock-view__search-clear"
                onClick={() => setSearchText('')}
                aria-label="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Resumo */}
        <div className="exemplar-stock-view__summary">
          <span>
            <strong>{filteredExemplars.length}</strong>{' '}
            {filteredExemplars.length === 1 ? 'exemplar' : 'exemplares'} ·{' '}
            <strong>{totalPieces}</strong> {totalPieces === 1 ? 'peça' : 'peças'}
          </span>
          <Link to="/" className="exemplar-stock-view__back-link">
            ← Voltar ao catálogo
          </Link>
        </div>

        {/* Conteúdo */}
        {error ? (
          <div className="exemplar-stock-view__error">
            <span className="exemplar-stock-view__error-icon">⚠️</span>
            <p className="exemplar-stock-view__error-message">{error}</p>
            <button
              type="button"
              className="exemplar-stock-view__retry-button"
              onClick={loadExemplars}
            >
              Tentar novamente
            </button>
          </div>
        ) : isLoading ? (
          <div className="exemplar-stock-view__loading">
            <div className="exemplar-stock-view__spinner" />
            <p>Carregando estoque...</p>
          </div>
        ) : filteredExemplars.length === 0 ? (
          <div className="exemplar-stock-view__empty">
            <span className="exemplar-stock-view__empty-icon">📦</span>
            <p>
              {searchText.trim()
                ? 'Nenhum exemplar encontrado com a busca atual.'
                : 'Nenhum exemplar cadastrado em estoque ainda.'}
            </p>
          </div>
        ) : (
          <div className="exemplar-stock-view__grid">
            {filteredExemplars.map((exemplar) => (
              <ExemplarCard key={exemplar.id} exemplar={exemplar} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Card de um exemplar
function ExemplarCard({ exemplar }: { exemplar: Exemplar }): React.ReactElement {
  const [photoFailed, setPhotoFailed] = useState(false);
  const hasPhoto = exemplar.photoFilepath && !photoFailed;

  return (
    <div className="exemplar-card">
      <div className="exemplar-card__photo">
        {hasPhoto ? (
          <img
            src={getThumbnailUrl(exemplar.photoFilepath!)}
            alt={exemplar.title}
            onError={() => setPhotoFailed(true)}
            loading="lazy"
          />
        ) : (
          <span className="exemplar-card__photo-placeholder">📦</span>
        )}
        <span className={`exemplar-card__quantity-badge ${exemplar.quantity === 0 ? 'exemplar-card__quantity-badge--empty' : ''}`}>
          {exemplar.quantity} {exemplar.quantity === 1 ? 'peça' : 'peças'}
        </span>
      </div>
      <div className="exemplar-card__info">
        <span className="exemplar-card__op">OP {exemplar.opNumber}</span>
        <h3 className="exemplar-card__title" title={exemplar.title}>{exemplar.title}</h3>
        {exemplar.book && (
          <Link
            to={`/?book=${exemplar.book.id}`}
            className="exemplar-card__project-link"
            title="Ver este projeto no catálogo"
          >
            📁 {exemplar.book.title}
          </Link>
        )}
        <div className="exemplar-card__location">
          📍 {exemplar.location}
          {exemplar.locationDetail ? ` — ${exemplar.locationDetail}` : ''}
        </div>
        {exemplar.notes && (
          <p className="exemplar-card__notes" title={exemplar.notes}>{exemplar.notes}</p>
        )}
      </div>
    </div>
  );
}

export default ExemplarStockView;
